<template>
	<ext-dialog
		v-model="dialogVisibility"
		:title="dialogTitle"
		:primaryButtonText="$t('save')"
		:secondaryButtonText="isEdit ? $t('delete') : ''"
		@primaryButtonAction="saveAction"
		@secondaryButtonAction="deleteAction"
		@closed="cancelAction"
	>
		<template #body>
			<div v-if="currentItem">
				<v-form ref="form" v-model="valid">
					<localized-text-field
						v-model="currentItem.name"
						:defaultLanguage="companyDefaultLanguage"
						:options="companyLanguageOptions"
						:rules="rules.name"
						context="packaging"
						:label="$t('name')"
					/>
					<v-text-field
						class="mt-4 rounded-xl"
						v-model="currentItem.prefix"
						:label="$t('prefix')"
						:rules="rules.prefix"
						dense
						outlined
					/>
				</v-form>
			</div>
		</template>
	</ext-dialog>
</template>

<script>
import uiMixin from "../../../../store/interfaces/ui.mixin";
import remote from "../../../../data/remote";
import { mapGetters } from "vuex";
import ExtDialog from "../../../partials/dialogs/ExtDialog.vue";
import LocalizedTextField from "../../../controls/LocalizedTextField.vue";

export default {
	mixins: [uiMixin],
	props: ["value", "item"],
	components: { ExtDialog, LocalizedTextField },
	data() {
		return {
			currentItem: null,
			valid: null,
			saving: false,
			deleting: false,
			rules: {
				name: [
					(v) =>
						v
							? Object.values(v).some((s) => (s || "").length > 100)
								? this.$t("$validation.max_length", { item: this.$t("name"), length: "100" })
								: true
							: this.$t("$validation.required", { item: this.$t("name") }),
				],
				prefix: [
					(v) =>
						(!!v && v.toString().trim().length > 0) || this.$t("$validation.required", { item: this.$t("prefix") }),
					(v) =>
						!v ||
						v.toString().length <= 50 ||
						this.$t("$validation.max_length", { item: this.$t("prefix"), length: "50" }),
					(v) => !v || /^[A-Za-z0-9_\-\.]+$/.test(v) || this.$t("$validation.invalid", { item: this.$t("prefix") }),
				],
			},
		};
	},
	computed: {
		...mapGetters(["company", "companyDefaultLanguage", "companyLanguageOptions"]),
		dialogVisibility: {
			get() {
				return this.value;
			},
			set(val) {
				this.$emit("input", val);
			},
		},
		isEdit() {
			return Boolean(this.currentItem && this.currentItem.id);
		},
		dialogTitle() {
			return this.isEdit ? this.$t("edit_packaging") : this.$t("add_new_packaging");
		},
	},
	watch: {
		value(v) {
			if (v) this.handleProps();
			else this.reset();
		},
		item: {
			immediate: true,
			handler() {
				if (this.value) this.handleProps();
			},
		},
	},
	methods: {
		handleProps() {
			const id = this.item && (this.item.id || this.item._id) ? this.item.id || this.item._id : null;
			const incomingName =
				this.item && this.item.name ? this.item.name : this.item && this.item.label ? this.item.label : {};
			const prefix =
				this.item && (this.item.prefix || this.item.code || this.item.key)
					? this.item.prefix || this.item.code || this.item.key
					: "";
			const base = { id, name: { ...incomingName }, prefix: prefix || "" };
			if (!base.name[this.companyDefaultLanguage]) base.name[this.companyDefaultLanguage] = "";
			for (const l of this.companyLanguageOptions) if (!base.name[l]) base.name[l] = "";
			if (!base.name._default) base.name._default = base.name[this.companyDefaultLanguage] || "";
			this.currentItem = base;
		},
		reset() {
			this.$refs.form?.reset();
			this.currentItem = null;
		},
		saveAction() {
			if (!this.valid) {
				this.$refs.form.validate();
				return;
			}
			if ((this.currentItem.name[this.companyDefaultLanguage] || "") === "") {
				this.showAlertDialog({
					message:
						this.$t("$validation.required_for_default_language", { item: this.$t("name") }) +
						` (${this.$t(`$language.${this.companyDefaultLanguage}`)})`,
				});
				return;
			}
			if ((this.currentItem.prefix || "").trim() === "") {
				this.$refs.form.validate();
				return;
			}
			this.currentItem.name._default =
				this.currentItem.name[this.companyDefaultLanguage] || this.currentItem.name._default || "";
			this.saving = true;
			const id = this.currentItem.id || null;
			const payload = {
				type: "PACKAGING",
				key: this.currentItem.prefix.trim(),
				code: this.currentItem.prefix.trim(),
				label: this.currentItem.name,
				isActive: true,
			};
			const onSuccess = (r) => {
				this.saving = false;
				this.$emit("saved", r);
				this.showSnackBar({
					message: this.$t(id ? "$message.updated" : "$message.created", { item: this.$t("packaging") }),
				});
			};
			const onFail = () => {
				this.saving = false;
			};
			if (id) {
				remote.references.update({ id, ...payload }, { onSuccess, onFail });
			} else {
				remote.references.create(payload, { onSuccess, onFail });
			}
		},
		deleteAction() {
			this.showConfirmDialog({
				message: this.$t("delete_confirm_message"),
				onConfirm: () => {
					this.deleting = true;
					const id = this.currentItem.id;
					remote.references.delete(id, {
						onSuccess: (r) => {
							this.deleting = false;
							this.$emit("deleted", r);
							this.showSnackBar({ message: this.$t("$message.deleted", { item: this.$t("packaging") }) });
						},
						onFail: () => {
							this.deleting = false;
						},
					});
				},
			});
		},
		cancelAction() {
			this.$emit("input", false);
			this.$emit("closed");
		},
	},
};
</script>
